import React, { useState, useEffect } from 'react';
import {
  Globe,
  Zap,
  Share2,
  QrCode,
  Check,
  ChevronRight,
  ShieldCheck,
  Video,
  Book,
  Award,
  ExternalLink,
  ArrowLeft,
  MessageSquare,
  Loader2,
  Settings,
} from 'lucide-react';
import { MentorAreaConfig, BioLinkItem, normalizeSocials, SocialEntry } from '../../types/mentorship';
import { BioQRCodeModal } from './BioQRCodeModal';
import { MentorAdminModal } from './MentorAdminModal';
import { mentorshipService } from '../../services/mentorshipService';
import { useAuth } from '../../contexts/AuthContext';
import {
  WhatsAppIcon,
  InstagramIcon,
  YouTubeIcon,
  LinkedInIcon,
  TikTokIcon,
  XTwitterIcon,
  GitHubIcon,
} from './SocialIcons';

interface BioLinkPageProps {
  config?: MentorAreaConfig;
  slug?: string;
  onBackToMain?: () => void;
}

export const BioLinkPage: React.FC<BioLinkPageProps> = ({ config, slug, onBackToMain }) => {
  const { user, isAdmin, isMentor } = useAuth();

  const targetSlug = (slug || config?.slug || '')
    .replace(/^\/?(mentoria-|bio-|links-|bio\/)?/, '')
    .toLowerCase();

  // Verifica se a config recebida via props já corresponde ao mentor real desejado
  const isDirectMatch = Boolean(
    config &&
    config.slug &&
    config.slug.replace(/^\/?(mentoria-|bio-|links-|bio\/)?/, '').toLowerCase() === targetSlug &&
    (targetSlug === 'vitor-terra' || config.mentorName !== 'Vitor Terra')
  );

  const [currentConfig, setCurrentConfig] = useState<MentorAreaConfig | null>(
    isDirectMatch ? (config as MentorAreaConfig) : null
  );

  const canEdit = Boolean(
    isAdmin || (isMentor && Boolean(currentConfig?.mentorUserId && user?.id === currentConfig.mentorUserId))
  );
  const [loading, setLoading] = useState<boolean>(!isDirectMatch && Boolean(targetSlug));
  const [notFound, setNotFound] = useState<boolean>(false);
  const [avatarError, setAvatarError] = useState<boolean>(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Buscar dados reais e atualizados do Supabase ao montar a página
  useEffect(() => {
    let isMounted = true;

    if (!targetSlug) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    setAvatarError(false);

    if (!isDirectMatch) {
      setLoading(true);
    }

    mentorshipService
      .fetchMentorshipConfig(targetSlug)
      .then((remoteCfg) => {
        if (isMounted) {
          if (remoteCfg) {
            setCurrentConfig(remoteCfg);
            setNotFound(false);
          } else {
            setNotFound(true);
          }
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar dados da bio:', err);
        if (isMounted && !currentConfig) {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [targetSlug, isDirectMatch]);

  const getInitials = (name?: string) => {
    if (!name) return 'MC';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // 1. Tela de Carregamento Ultra-Fluida (Skeleton) - Previne qualquer flash de dados antigos
  if (loading || (!currentConfig && !notFound)) {
    return (
      <div className="min-h-screen bg-[#0d0e11] text-white flex flex-col items-center justify-between p-4 md:py-10 select-none relative overflow-x-hidden">
        {/* Dynamic Background Glow Spots */}
        <div
          className="fixed top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[140px] opacity-10 bg-[#0FFF55] pointer-events-none"
        />
        <div className="fixed bottom-0 right-0 w-[350px] h-[350px] rounded-full blur-[120px] opacity-5 bg-cyan-500 pointer-events-none" />

        {/* Top Floating Back Button */}
        <div className="w-full max-w-md flex items-center justify-between z-20 pb-2">
          <button
            onClick={() => {
              window.history.pushState(null, '', '/');
              if (onBackToMain) onBackToMain();
              else window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#17181c]/90 border border-white/10 text-xs font-semibold text-gray-400 backdrop-blur-md"
          >
            <ArrowLeft className="w-4 h-4 text-[#0FFF55]" />
            <span>Voltar para MasterClub</span>
          </button>
        </div>

        {/* Skeleton Smartphone Container */}
        <div className="w-full max-w-md flex flex-col items-center gap-6 z-10 py-2">
          {/* Profile Header Skeleton */}
          <div className="flex flex-col items-center text-center gap-3 w-full">
            {/* Avatar skeleton */}
            <div className="relative">
              <div className="w-28 h-28 md:w-32 md:h-32 rounded-full p-1 bg-[#121316] border-2 border-white/10 shadow-2xl overflow-hidden flex items-center justify-center">
                <div className="w-full h-full rounded-full bg-white/5 animate-pulse flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-[#0FFF55]/60 animate-spin" />
                </div>
              </div>
            </div>

            {/* Name & Subtitle Skeleton */}
            <div className="flex flex-col items-center gap-2 mt-1">
              <div className="w-44 h-7 rounded-lg bg-white/10 animate-pulse" />
              <div className="w-64 h-3.5 rounded-md bg-white/5 animate-pulse mt-1" />
            </div>

            {/* Social Media Icons Skeleton */}
            <div className="flex items-center justify-center gap-3.5 mt-2">
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 animate-pulse" />
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 animate-pulse" />
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 animate-pulse" />
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 animate-pulse" />
            </div>
          </div>

          {/* Links Cards Skeleton */}
          <div className="w-full flex flex-col gap-3.5 mt-2">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="w-full py-4 px-6 rounded-full bg-[#141519]/80 border border-white/10 flex items-center justify-between gap-4 shadow-lg animate-pulse"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex-shrink-0" />
                  <div className="flex flex-col gap-1.5 flex-1">
                    <div className="w-3/4 h-4 rounded bg-white/10" />
                    <div className="w-1/2 h-2.5 rounded bg-white/5" />
                  </div>
                </div>
                <div className="w-4 h-4 rounded bg-white/10" />
              </div>
            ))}
          </div>

          {/* Bottom Actions Skeleton */}
          <div className="flex items-center justify-center gap-3 w-full pt-4 border-t border-white/10 mt-4">
            <div className="flex-1 py-3.5 rounded-full bg-white/5 border border-white/5 animate-pulse h-11" />
            <div className="flex-1 py-3.5 rounded-full bg-[#17181c] border border-white/5 animate-pulse h-11" />
          </div>

          {/* MasterClub Branding Footer */}
          <div className="flex items-center gap-2 pt-4 text-[11px] font-bold text-gray-500">
            <span>Powered by</span>
            <div className="px-2.5 py-0.5 rounded-full bg-[#0FFF55]/80 text-black font-black text-[10px] uppercase">
              MasterClub
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. Tela de Mentor Não Encontrado
  if (notFound && !currentConfig) {
    return (
      <div className="min-h-screen bg-[#0d0e11] text-white flex flex-col items-center justify-center p-6 text-center select-none relative overflow-hidden">
        <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full blur-[140px] opacity-10 bg-[#0FFF55] pointer-events-none" />
        <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-[#0FFF55] mb-4 shadow-xl">
          <Globe className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Mentor ou Página não encontrada</h2>
        <p className="text-xs text-gray-400 max-w-sm mb-6 leading-relaxed">
          O link de bio acessado não foi localizado ou ainda não foi configurado pelo mentor.
        </p>
        <button
          onClick={() => {
            window.history.pushState(null, '', '/');
            if (onBackToMain) onBackToMain();
            else window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#0FFF55] text-black font-extrabold text-xs hover:bg-[#0dd149] transition-all shadow-lg shadow-[#0FFF55]/20 active:scale-95"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar para a MasterClub</span>
        </button>
      </div>
    );
  }

  const activeConfig = currentConfig || config || ({} as MentorAreaConfig);

  const ensureExternalUrl = (url?: string) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://${trimmed}`;
  };

  const bio = activeConfig.bioConfig || {
    isEnabled: true,
    slug: activeConfig.slug,
    bioTitle: activeConfig.mentorName,
    bioSubtitle: activeConfig.mentorTitle,
    bioAvatarUrl: activeConfig.mentorAvatarUrl,
    socials: {},
    links: [
      {
        id: '1',
        title: `⚡ Mentoria ${activeConfig.courseTitle}`,
        subtitle: 'Programa completo com acompanhamento individual',
        url: `/mentoria-${activeConfig.slug}`,
        icon: 'Zap',
      },
      {
        id: '2',
        title: '💬 Atendimento Direct no WhatsApp',
        subtitle: 'Fale com a equipe para tirar dúvidas',
        url: 'https://wa.me/5511999999999',
        icon: 'MessageSquare',
      },
      {
        id: '3',
        title: '🌐 Ecossistema MasterClub VIP',
        subtitle: 'Acesse a plataforma oficial',
        url: '/',
        icon: 'Globe',
      },
    ],
  };

  // Normaliza socials para sempre usar SocialEntry[]
  const normalizedSocials = normalizeSocials((bio.socials || {}) as any);

  const accentColor = activeConfig.branding?.accentColor || bio.theme?.accentColor || '#0FFF55';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : `https://masterclub.vip/bio-${activeConfig.slug}`;
  const avatarUrl = bio.bioAvatarUrl || activeConfig.mentorAvatarUrl || '';

  const getLinkIcon = (iconName?: string, customImgUrl?: string, title?: string) => {
    const rawImg =
      customImgUrl ||
      (iconName &&
      (iconName.startsWith('http://') ||
        iconName.startsWith('https://') ||
        iconName.startsWith('data:image/') ||
        iconName.startsWith('/'))
        ? iconName
        : null);

    if (rawImg) {
      return (
        <img
          src={rawImg}
          alt={title || 'Ícone'}
          className="w-full h-full object-cover rounded-full select-none pointer-events-none"
          onError={(e) => {
            (e.target as HTMLElement).style.display = 'none';
          }}
        />
      );
    }

    switch (iconName) {
      case 'Zap':
      case 'Lightning':
        return <Zap className="w-5 h-5 transition-colors group-hover:text-black" style={{ color: accentColor }} />;
      case 'MessageSquare':
      case 'WhatsApp':
      case 'whatsapp':
        return <WhatsAppIcon className="w-5 h-5 text-[#25D366] group-hover:text-black transition-colors" />;
      case 'Globe':
      case 'Website':
      case 'Web':
        return <Globe className="w-5 h-5 text-cyan-400 group-hover:text-black transition-colors" />;
      case 'Video':
      case 'Youtube':
      case 'youtube':
        return <YouTubeIcon className="w-5 h-5 text-[#FF0000] group-hover:text-black transition-colors" />;
      case 'Instagram':
      case 'instagram':
        return <InstagramIcon className="w-5 h-5 text-[#E4405F] group-hover:text-black transition-colors" />;
      case 'LinkedIn':
      case 'linkedin':
        return <LinkedInIcon className="w-5 h-5 text-[#0A66C2] group-hover:text-black transition-colors" />;
      case 'TikTok':
      case 'tiktok':
        return <TikTokIcon className="w-5 h-5 text-[#00f2fe] group-hover:text-black transition-colors" />;
      case 'XTwitter':
      case 'Twitter':
      case 'twitter':
        return <XTwitterIcon className="w-5 h-5 text-white group-hover:text-black transition-colors" />;
      case 'GitHub':
      case 'github':
        return <GitHubIcon className="w-5 h-5 text-white group-hover:text-black transition-colors" />;
      case 'Book':
        return <Book className="w-5 h-5 text-purple-400 group-hover:text-black transition-colors" />;
      case 'Award':
        return <Award className="w-5 h-5 text-amber-400 group-hover:text-black transition-colors" />;
      default:
        return <ExternalLink className="w-5 h-5 group-hover:text-black transition-colors" style={{ color: accentColor }} />;
    }
  };

  const handleSharePage = () => {
    navigator.clipboard.writeText(pageUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#0d0e11] text-white flex flex-col items-center justify-between p-4 md:py-10 select-none relative overflow-x-hidden">
      {/* Dynamic Background Glow Spots */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[140px] opacity-15 pointer-events-none"
        style={{ backgroundColor: accentColor }}
      />
      <div className="fixed bottom-0 right-0 w-[350px] h-[350px] rounded-full blur-[120px] opacity-10 bg-cyan-500 pointer-events-none" />



      {/* Top Floating Back Button */}
      <div className="w-full max-w-md flex items-center justify-between z-20 pb-2">
        <button
          onClick={() => {
            window.history.pushState(null, '', '/');
            if (onBackToMain) {
              onBackToMain();
            } else {
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
          }}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#17181c]/90 hover:bg-[#22242b] border border-white/15 text-xs font-semibold text-gray-300 hover:text-white transition-all backdrop-blur-md shadow-lg"
        >
          <ArrowLeft className="w-4 h-4 text-[#0FFF55]" />
          <span>Voltar para MasterClub</span>
        </button>
      </div>

      {/* Main Smartphone Container */}
      <div className="w-full max-w-md flex flex-col items-center gap-6 z-10 py-2">
        {/* Profile Header */}
        <div className="flex flex-col items-center text-center gap-3 w-full">
          {/* Avatar with Glow Ring (Selo removido da foto) */}
          <div className="relative group cursor-pointer">
            <div
              className="absolute -inset-1 rounded-full blur-md opacity-75 group-hover:opacity-100 transition-opacity animate-pulse"
              style={{ backgroundColor: accentColor }}
            />
            <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full p-1 bg-[#121316] border-2 border-white/20 shadow-2xl overflow-hidden flex items-center justify-center">
              {avatarUrl && !avatarError ? (
                <img
                  src={avatarUrl}
                  alt={bio.bioTitle}
                  onError={() => setAvatarError(true)}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-[#17181c] flex items-center justify-center font-black text-2xl md:text-3xl text-[#0FFF55] tracking-wider select-none">
                  {getInitials(bio.bioTitle || activeConfig.mentorName)}
                </div>
              )}
            </div>
          </div>

          {/* Name & Title (Selo Mantido Exclusivamente na Frente do Nome) */}
          <div>
            <div className="flex items-center justify-center gap-1.5">
              <h1 className="text-2xl font-black text-white tracking-tight">{bio.bioTitle}</h1>
              <ShieldCheck className="w-4.5 h-4.5" style={{ color: accentColor }} />
            </div>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto leading-relaxed font-medium">
              {bio.bioSubtitle}
            </p>
          </div>

          {/* Social Media Icons Bar — Suporte a múltiplos links por rede */}
          <div className="flex items-center justify-center gap-2.5 mt-2 flex-wrap">
            {([
              {
                key: 'instagram' as const,
                render: (entry: SocialEntry, i: number) => (
                  <a
                    key={`ig-${i}`}
                    href={ensureExternalUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group flex flex-col items-center"
                    title={entry.label || 'Instagram'}
                  >
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(228,64,95,0.4)] hover:border-[#E4405F]/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#833ab4]/30 via-[#fd1d1d]/30 to-[#fcb045]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <InstagramIcon className="w-5 h-5 text-[#E4405F] relative z-10 filter drop-shadow" />
                    </div>
                    {entry.label && (
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {entry.label}
                      </span>
                    )}
                  </a>
                ),
              },
              {
                key: 'youtube' as const,
                render: (entry: SocialEntry, i: number) => (
                  <a
                    key={`yt-${i}`}
                    href={ensureExternalUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group flex flex-col items-center"
                    title={entry.label || 'YouTube'}
                  >
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(255,0,0,0.4)] hover:border-[#FF0000]/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                      <div className="absolute inset-0 bg-[#FF0000]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <YouTubeIcon className="w-5 h-5 text-[#FF0000] relative z-10 filter drop-shadow" />
                    </div>
                    {entry.label && (
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {entry.label}
                      </span>
                    )}
                  </a>
                ),
              },
              {
                key: 'whatsapp' as const,
                render: (entry: SocialEntry, i: number) => {
                  const whatsappUrl = ensureExternalUrl(
                    entry.url.startsWith('http') ? entry.url : `https://wa.me/${entry.url.replace(/\D/g, '')}`
                  );
                  return (
                    <a
                      key={`wa-${i}`}
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative group flex flex-col items-center"
                      title={entry.label || 'WhatsApp'}
                    >
                      <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] hover:border-[#25D366]/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                        <div className="absolute inset-0 bg-[#25D366]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <WhatsAppIcon className="w-5 h-5 text-[#25D366] relative z-10 filter drop-shadow" />
                      </div>
                      {entry.label && (
                        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                          {entry.label}
                        </span>
                      )}
                    </a>
                  );
                },
              },
              {
                key: 'linkedin' as const,
                render: (entry: SocialEntry, i: number) => (
                  <a
                    key={`li-${i}`}
                    href={ensureExternalUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group flex flex-col items-center"
                    title={entry.label || 'LinkedIn'}
                  >
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(10,102,194,0.4)] hover:border-[#0A66C2]/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                      <div className="absolute inset-0 bg-[#0A66C2]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <LinkedInIcon className="w-5 h-5 text-[#0A66C2] relative z-10 filter drop-shadow" />
                    </div>
                    {entry.label && (
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {entry.label}
                      </span>
                    )}
                  </a>
                ),
              },
              {
                key: 'tiktok' as const,
                render: (entry: SocialEntry, i: number) => (
                  <a
                    key={`tt-${i}`}
                    href={ensureExternalUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group flex flex-col items-center"
                    title={entry.label || 'TikTok'}
                  >
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] hover:border-white/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                      <div className="absolute inset-0 bg-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <TikTokIcon className="w-5 h-5 text-white relative z-10 filter drop-shadow" />
                    </div>
                    {entry.label && (
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {entry.label}
                      </span>
                    )}
                  </a>
                ),
              },
              {
                key: 'twitter' as const,
                render: (entry: SocialEntry, i: number) => (
                  <a
                    key={`tw-${i}`}
                    href={ensureExternalUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group flex flex-col items-center"
                    title={entry.label || 'X (Twitter)'}
                  >
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] hover:border-white/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                      <div className="absolute inset-0 bg-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <XTwitterIcon className="w-5 h-5 text-white relative z-10 filter drop-shadow" />
                    </div>
                    {entry.label && (
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {entry.label}
                      </span>
                    )}
                  </a>
                ),
              },
              {
                key: 'github' as const,
                render: (entry: SocialEntry, i: number) => (
                  <a
                    key={`gh-${i}`}
                    href={ensureExternalUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group flex flex-col items-center"
                    title={entry.label || 'GitHub'}
                  >
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] hover:border-white/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                      <div className="absolute inset-0 bg-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <GitHubIcon className="w-5 h-5 text-white relative z-10 filter drop-shadow" />
                    </div>
                    {entry.label && (
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {entry.label}
                      </span>
                    )}
                  </a>
                ),
              },
              {
                key: 'website' as const,
                render: (entry: SocialEntry, i: number) => (
                  <a
                    key={`ws-${i}`}
                    href={ensureExternalUrl(entry.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group flex flex-col items-center"
                    title={entry.label || 'Website'}
                  >
                    <div className="p-3 rounded-2xl bg-gradient-to-br from-white/15 via-white/5 to-transparent backdrop-blur-xl border border-white/25 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(15,255,85,0.4)] hover:border-[#0FFF55]/60 transition-all duration-300 hover:scale-115 active:scale-95 overflow-hidden">
                      <div className="absolute inset-0 bg-[#0FFF55]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <Globe className="w-5 h-5 text-[#0FFF55] relative z-10 filter drop-shadow" />
                    </div>
                    {entry.label && (
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {entry.label}
                      </span>
                    )}
                  </a>
                ),
              },
            ] as const).map(({ key, render }) =>
              (normalizedSocials[key] || []).map((entry, i) => render(entry, i))
            )}
          </div>
        </div>

        {/* Links Cards List (Padronizados 100% sem badges) */}
        <div className="w-full flex flex-col gap-3.5 mt-2">
          {bio.links.map((link: BioLinkItem) => {
            return (
              <a
                key={link.id}
                href={link.url}
                target={link.url.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="relative group w-full py-4 px-6 rounded-full bg-[#141519] border border-[#0FFF55]/50 flex items-center justify-between gap-4 shadow-lg transition-all duration-300 active:scale-[0.98] overflow-hidden cursor-pointer hover:border-[#0FFF55] hover:shadow-[0_0_20px_rgba(15,255,85,0.4)]"
              >
                {/* Sliding Green Hover Background Effect */}
                <div className="absolute inset-0 bg-[#0FFF55] -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out pointer-events-none" />

                {/* Left Icon & Text */}
                <div className="relative z-10 flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-white/5 border border-[#0FFF55]/30 flex items-center justify-center flex-shrink-0 group-hover:border-black/20 group-hover:bg-black/10 transition-all duration-300 overflow-hidden">
                    {getLinkIcon(link.icon, link.thumbnailUrl || link.iconUrl, link.title)}
                  </div>

                  <div className="flex flex-col text-left truncate">
                    <span className="text-sm font-extrabold text-white group-hover:text-black leading-snug truncate transition-colors duration-300">
                      {link.title}
                    </span>
                    {link.subtitle && (
                      <span className="text-[11px] text-gray-400 group-hover:text-black/80 truncate mt-0.5 transition-colors duration-300 font-medium">
                        {link.subtitle}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right Arrow Chevron */}
                <ChevronRight className="relative z-10 w-4.5 h-4.5 text-gray-400 group-hover:text-black group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
              </a>
            );
          })}
        </div>

        {/* Bottom Actions Row: Compartilhar & QR Code */}
        <div className="flex items-center justify-center gap-3 w-full pt-4 border-t border-white/10 mt-4">
          <button
            onClick={handleSharePage}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold text-gray-200 transition-all active:scale-95"
          >
            {copiedLink ? (
              <>
                <Check className="w-4 h-4 text-[#0FFF55]" />
                <span className="text-[#0FFF55]">Link Copiado!</span>
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4 text-gray-400" />
                <span>Compartilhar</span>
              </>
            )}
          </button>

          <button
            onClick={() => setIsQrModalOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#17181c] hover:bg-[#22242b] border border-white/15 text-xs font-extrabold text-white transition-all active:scale-95"
          >
            <QrCode className="w-4 h-4 text-[#0FFF55]" />
            <span>QR Code</span>
          </button>
        </div>

        {/* MasterClub Branding Footer */}
        <div className="flex flex-col items-center gap-1.5 pt-4 text-center">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState(null, '', '/');
              if (onBackToMain) {
                onBackToMain();
              } else {
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            }}
            className="flex items-center gap-2 text-[11px] font-bold text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <span>Powered by</span>
            <div className="px-2.5 py-0.5 rounded-full bg-[#0FFF55] text-black font-black text-[10px] uppercase tracking-wider">
              MasterClub
            </div>
          </a>
        </div>
      </div>

      {/* Modal QR Code */}
      <BioQRCodeModal
        isOpen={isQrModalOpen}
        onClose={() => setIsQrModalOpen(false)}
        title={bio.bioTitle}
        url={pageUrl}
        avatarUrl={avatarUrl}
        accentColor={accentColor}
      />

      {/* Botão Flutuante de Edição Rápida para Administrador ou Mentor */}
      {canEdit && (
        <button
          onClick={() => setIsAdminModalOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs shadow-2xl shadow-[#0FFF55]/30 transition-all hover:scale-105 active:scale-95 border-2 border-black/40"
          title="Editar links e configurações desta Bio"
        >
          <Settings className="w-4 h-4" />
          <span>Editar Bio</span>
        </button>
      )}

      {/* Modal de Configuração Administrativa */}
      {canEdit && (
        <MentorAdminModal
          isOpen={isAdminModalOpen}
          onClose={() => setIsAdminModalOpen(false)}
          config={activeConfig}
          initialTab="bio"
          onSaveConfig={async (newConfig) => {
            setCurrentConfig(newConfig);
            const targetSlug = newConfig.slug || slug || activeConfig.slug;
            await mentorshipService.saveMentorshipConfigToSupabase(targetSlug, newConfig);
          }}
        />
      )}
    </div>
  );
};
